// Survey and report storage operations

import { 
    STORES, 
    openDatabase, 
    getAllFromStore, 
    getFromStore, 
    putToStore, 
    deleteFromStore, 
    clearStore,
    clearAllStores
} from './db-core';
import { syncHistoryStorage } from './sync-history-storage';

interface PendingSurvey {
    id: string;
    surveyType?: string;
    hazardType?: string;
    residentName: string;
    propertyAddress: string;
    workOrderId?: string;
    caseNumber?: string;
    isAppointmentConfirmed?: boolean;
    dueDate: string;
    uprn?: string;
    residentPhoneNumber?: string;
    residentEmailAddress?: string;
    notes?: string;
    createdAt: string;
}

interface LocalSurveyReport {
    surveyId: string;
    pendingAttemptId?: string;
    reportId: string;
    document: unknown;
    hazard?: unknown;
    startedAt: string;
    lastModifiedAt: string;
    isSynced: boolean;
    syncedAt?: string;
    isComplete: boolean;
    userId: string;
    cachedSurveyDetails?: PendingSurvey;
    outboxStatus: number;
    outboxAddedAt?: string;
    syncAttemptCount: number;
    lastSyncError?: string;
}

interface LocalSurveyBounce {
    bounceId: string;
    surveyId: string;
    pendingAttemptId: string;
    reason: string;
    userId: string;
    occurredAtClient: string;
    queuedAt: string;
    status: number;
    attemptCount: number;
    lastError?: string;
}

export const indexedDbStorage = {
    async initialize(): Promise<boolean> {
        try {
            await openDatabase();
            return true;
        } catch (error) {
            console.error('[IndexedDB] Initialize failed:', error);
            return false;
        }
    },

    async getCachedSurveys(): Promise<string> {
        try {
            const surveys = await getAllFromStore<PendingSurvey>(STORES.CACHED_SURVEYS);
            return JSON.stringify(surveys);
        } catch (error) {
            console.error('[IndexedDB] getCachedSurveys failed:', error);
            return '[]';
        }
    },

    async getCachedSurvey(surveyId: string): Promise<string | null> {
        try {
            const survey = await getFromStore<PendingSurvey>(STORES.CACHED_SURVEYS, surveyId);
            return survey ? JSON.stringify(survey) : null;
        } catch (error) {
            console.error('[IndexedDB] getCachedSurvey failed:', error);
            return null;
        }
    },

    async cacheSurveys(surveysJson: string): Promise<boolean> {
        try {
            const surveys = JSON.parse(surveysJson) as PendingSurvey[];
            await clearStore(STORES.CACHED_SURVEYS);
            for (const survey of surveys) {
                await putToStore(STORES.CACHED_SURVEYS, survey);
            }
            return true;
        } catch (error) {
            console.error('[IndexedDB] cacheSurveys failed:', error);
            return false;
        }
    },

    async getLocalReports(): Promise<string> {
        try {
            const reports = await getAllFromStore<LocalSurveyReport>(STORES.LOCAL_REPORTS);
            return JSON.stringify(reports);
        } catch (error) {
            console.error('[IndexedDB] getLocalReports failed:', error);
            return '[]';
        }
    },

    async getLocalReport(surveyId: string): Promise<string | null> {
        try {
            const report = await getFromStore<LocalSurveyReport>(STORES.LOCAL_REPORTS, surveyId);
            return report ? JSON.stringify(report) : null;
        } catch (error) {
            console.error('[IndexedDB] getLocalReport failed:', error);
            return null;
        }
    },

    async saveLocalReport(reportJson: string): Promise<boolean> {
        try {
            const report = JSON.parse(reportJson) as LocalSurveyReport;
            await putToStore(STORES.LOCAL_REPORTS, report);
            return true;
        } catch (error) {
            console.error('[IndexedDB] saveLocalReport failed:', error);
            return false;
        }
    },

    async deleteLocalReport(surveyId: string): Promise<boolean> {
        try {
            await deleteFromStore(STORES.LOCAL_REPORTS, surveyId);
            return true;
        } catch (error) {
            console.error('[IndexedDB] deleteLocalReport failed:', error);
            return false;
        }
    },

    async getPendingSyncReports(): Promise<string> {
        try {
            const reports = await getAllFromStore<LocalSurveyReport>(STORES.LOCAL_REPORTS);
            const pending = reports.filter(r => r.isComplete && !r.isSynced);
            return JSON.stringify(pending);
        } catch (error) {
            console.error('[IndexedDB] getPendingSyncReports failed:', error);
            return '[]';
        }
    },

    async markReportAsSynced(surveyId: string): Promise<boolean> {
        try {
            const report = await getFromStore<LocalSurveyReport>(STORES.LOCAL_REPORTS, surveyId);
            if (report) {
                report.isSynced = true;
                report.syncedAt = new Date().toISOString();
                await putToStore(STORES.LOCAL_REPORTS, report);
            }
            return true;
        } catch (error) {
            console.error('[IndexedDB] markReportAsSynced failed:', error);
            return false;
        }
    },

    async queueBounce(bounceJson: string): Promise<boolean> {
        try {
            const bounce = JSON.parse(bounceJson) as LocalSurveyBounce;
            const db = await openDatabase();
            const transaction = db.transaction(
                [STORES.BOUNCE_OUTBOX, STORES.CACHED_SURVEYS, STORES.LOCAL_REPORTS],
                'readwrite');
            transaction.objectStore(STORES.BOUNCE_OUTBOX).put(bounce);
            transaction.objectStore(STORES.CACHED_SURVEYS).delete(bounce.surveyId);
            const request = transaction.objectStore(STORES.LOCAL_REPORTS).get(bounce.surveyId);
            request.onsuccess = () => {
                const report = request.result as LocalSurveyReport | undefined;
                if (report && (!report.isComplete || report.outboxStatus === 0)) {
                    transaction.objectStore(STORES.LOCAL_REPORTS).delete(bounce.surveyId);
                }
            };
            return await new Promise<boolean>((resolve, reject) => {
                transaction.oncomplete = () => resolve(true);
                transaction.onerror = () => reject(transaction.error);
                transaction.onabort = () => reject(transaction.error || new Error('Bounce transaction aborted'));
            });
        } catch (error) {
            console.error('[IndexedDB] queueBounce failed:', error);
            return false;
        }
    },

    async getBounceOutbox(): Promise<string> {
        try {
            const items = await getAllFromStore<LocalSurveyBounce>(STORES.BOUNCE_OUTBOX);
            return JSON.stringify(items.filter(item => item.status !== 3));
        } catch (error) {
            console.error('[IndexedDB] getBounceOutbox failed:', error);
            return '[]';
        }
    },

    async updateBounce(bounceId: string, status: number, errorMessage?: string): Promise<boolean> {
        try {
            const bounce = await getFromStore<LocalSurveyBounce>(STORES.BOUNCE_OUTBOX, bounceId);
            if (bounce) {
                bounce.status = status;
                bounce.lastError = errorMessage;
                if (status === 4) bounce.attemptCount++;
                await putToStore(STORES.BOUNCE_OUTBOX, bounce);
            }
            return true;
        } catch (error) {
            console.error('[IndexedDB] updateBounce failed:', error);
            return false;
        }
    },

    // Sync history operations (delegated to sync-history-storage)
    getSyncEvents: syncHistoryStorage.getSyncEvents.bind(syncHistoryStorage),
    getSyncEvent: syncHistoryStorage.getSyncEvent.bind(syncHistoryStorage),
    saveSyncEvent: syncHistoryStorage.saveSyncEvent.bind(syncHistoryStorage),
    addSyncLogEntry: syncHistoryStorage.addSyncLogEntry.bind(syncHistoryStorage),
    clearSyncHistory: syncHistoryStorage.clearSyncHistory.bind(syncHistoryStorage),

    // Clear all data
    async clearAllData(): Promise<boolean> {
        try {
            await clearAllStores();
            console.log('[IndexedDB] All data cleared');
            return true;
        } catch (error) {
            console.error('[IndexedDB] clearAllData failed:', error);
            return false;
        }
    }
};
